
import { useState, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import NavBar from "./NavBar";
import DiscordLogin from "./DiscordLogin";
import { AnimatePresence } from "framer-motion";
import { supabase, forceRefreshUserProfile } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import { toast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { clearAuthStorage } from "@/utils/authCleanup";

const Layout = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [initializing, setInitializing] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [profileRefreshAttempted, setProfileRefreshAttempted] = useState(false);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log("Auth state changed:", event, newSession ? "with session" : "no session");
        setSession(newSession);
        
        if (event === 'SIGNED_IN') {
          console.log("User signed in successfully");
          
          // Only attempt profile creation if not already attempted
          if (newSession?.user?.id && !profileRefreshAttempted) {
            setProfileRefreshAttempted(true);
            try {
              console.log("Attempting to create profile for user:", newSession.user.id);
              
              // Using the database function to handle profile creation/update
              const profileCreated = await forceRefreshUserProfile(newSession.user.id);
              
              if (profileCreated) {
                console.log("User profile created/refreshed successfully");
                
                // Invalidate any cached profile data - FIXED: Using object with queryKey property
                queryClient.invalidateQueries({ queryKey: ['profile'] });
                
                // Verify profile was actually created
                const { data: profileData, error: profileError } = await supabase
                  .from('profiles')
                  .select('*')
                  .eq('id', newSession.user.id)
                  .single();
                  
                if (profileError) {
                  console.error("Error verifying profile creation:", profileError);
                } else {
                  console.log("Verified profile exists:", profileData);
                }
                
              } else {
                console.error("Failed to create/refresh user profile");
              }
            } catch (error) {
              console.error("Exception during profile creation:", error);
              // Don't clear storage on profile creation errors
            }
          }
          
          toast({
            title: "Login successful",
            description: "Welcome to PricePal!",
          });
        } else if (event === 'SIGNED_OUT') {
          console.log("User signed out");
          
          // Clear all auth-related storage and queries
          clearAuthStorage(queryClient);
          
          toast({
            title: "Logged out",
            description: "You have been successfully logged out.",
          });
          navigate('/');
        } else if (event === 'TOKEN_REFRESHED') {
          console.log("Auth token refreshed");
        } else if (event === 'USER_UPDATED') {
          console.log("User data updated");
          // Invalidate profile queries when user data changes - FIXED: Using object with queryKey property
          queryClient.invalidateQueries({ queryKey: ['profile'] });
        }
      }
    );

    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        console.log("Initial session retrieved:", session ? "with session" : "no session");
        
        if (error) {
          console.error("Error getting initial session:", error);
          clearAuthStorage(queryClient);
          setSession(null);
        } else {
          setSession(session);
          
          // Create/refresh profile for existing session if needed
          if (session?.user?.id && !profileRefreshAttempted) {
            setProfileRefreshAttempted(true);
            try {
              const profileCreated = await forceRefreshUserProfile(session.user.id);
              if (!profileCreated) {
                console.error("Failed to create/refresh profile during init");
              }
            } catch (error) {
              console.error("Error creating profile during init:", error);
              // Don't clear storage on profile creation errors
            }
          }
        }
      } catch (error) {
        console.error("Error getting session:", error);
        clearAuthStorage(queryClient);
        setSession(null);
      } finally {
        setInitializing(false);
      }
    };

    getInitialSession();

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, queryClient, profileRefreshAttempted]);

  if (initializing) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <>
      <AnimatePresence mode="wait">
        {!session && <DiscordLogin onSuccess={handleLoginSuccess} />}
      </AnimatePresence>

      <div className="flex flex-col min-h-screen">
        <NavBar />
        <main className="flex-grow pt-24 pb-20">
          <div className="app-container">
            <Outlet />
          </div>
        </main>
      </div>
    </>
  );
  
  // Simple handler for login success
  function handleLoginSuccess() {
    console.log("Login success callback triggered");
  }
};

export default Layout;
